import { useState } from 'react';
import dayjs from 'dayjs';
import LogDisplay from 'client/js/util/log_display';
import ErrorLogFilters from './error_log_filters';

// Validation functions for error log filters
const validateFilters = (filters) => {
  const errors = {};

  // Hostname validation
  if (filters.hostname) {
    const hostname = filters.hostname.trim();
    if (hostname.length > 100) {
      errors.hostname = 'Hostname zu lang (max. 100 Zeichen)';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(hostname)) {
      errors.hostname = 'Ungültiges Hostname-Format (nur alphanumerisch, Punkte, Bindestriche, Unterstriche)';
    }
  }

  // Client IP validation
  if (filters.client_ip) {
    const ip = filters.client_ip.trim();
    if (ip.length > 45) {
      errors.client_ip = 'Client IP-Filter zu lang (max. 45 Zeichen)';
    } else if (!/^[a-fA-F0-9.:]+$/.test(ip)) {
      errors.client_ip = 'Ungültiges IP-Format (IPv4/IPv6-Adressen und partielle Übereinstimmungen)';
    }
  }

  // Log level validation
  if (filters.log_level) {
    const level = filters.log_level.trim().toLowerCase();
    const validLevels = ['emergency', 'alert', 'critical', 'error', 'warning', 'notice', 'info', 'debug'];
    if (!validLevels.includes(level)) {
      errors.log_level = `Ungültiger Log-Level (muss einer sein von: ${validLevels.join(', ')})`;
    }
  }

  // Module validation
  if (filters.module) {
    const module = filters.module.trim();
    if (module.length > 50) {
      errors.module = 'Modul zu lang (max. 50 Zeichen)';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(module)) {
      errors.module = 'Ungültiges Modul-Format (nur alphanumerisch, Unterstriche, Bindestriche)';
    }
  }

  // Time validation
  if (filters.from_time) {
    try {
      const parsedTime = new Date(filters.from_time);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      if (isNaN(parsedTime.getTime())) {
        errors.from_time = 'Ungültiges Zeitformat';
      } else if (parsedTime > new Date()) {
        errors.from_time = 'Zeit kann nicht in der Zukunft liegen';
      } else if (parsedTime < sevenDaysAgo - (60 * 1000)) { // Allow 1 minute tolerance
        errors.from_time = 'Zeit kann nicht älter als 7 Tage sein';
      }
    } catch (e) {
      errors.from_time = 'Ungültiges Zeitformat';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

// Get color classes for log level
const getLogLevelClasses = (logLevel) => {
  const lowerLevel = logLevel?.toLowerCase();
  switch (lowerLevel) {
    case 'emergency':
    case 'alert':
    case 'critical':
      return { border: 'border-log-critical', badge: 'badge-log-critical' };
    case 'error':
      return { border: 'border-log-error', badge: 'badge-log-error' };
    case 'warning':
      return { border: 'border-log-warning', badge: 'badge-log-warning' };
    case 'notice':
    case 'info':
      return { border: 'border-log-info', badge: 'badge-log-info' };
    case 'debug':
      return { border: 'border-log-debug', badge: 'badge-log-debug' };
    default:
      return { border: 'border-log-unknown', badge: 'badge-log-unknown' };
  }
};

const Index = ({ endpoint = '/usercp/error_logs', queryKey = 'error_logs' }) => {
  const [filters, setFilters] = useState({
    hostname: '',
    client_ip: '',
    log_level: '',
    module: '',
    from_time: '',
    to_time: ''
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      hostname: '',
      client_ip: '',
      log_level: '',
      module: '',
      from_time: '',
      to_time: ''
    });
  };

  const renderLogItem = (item) => {
    const levelClasses = getLogLevelClasses(item.log_level);
    const cleanIp = item.client_ip?.replace(/^::ffff:/, '') || item.client_ip || 'N/A';

    return (
      <>
        <span className="log-timestamp">
          {dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}
        </span>
        <span className="log-client-ip">
          {cleanIp}
        </span>
        <span className="log-hostname">
          {item.hostname || 'N/A'}
        </span>
        <span className="log-module">
          {item.module || 'N/A'}
        </span>
        <span className="log-message">
          {item.message || 'No message'}
        </span>
        <span className={`log-level ${levelClasses.badge}`}>
          {item.log_level?.toUpperCase() || 'UNKNOWN'}
        </span>
      </>
    );
  };

  const renderLogDetails = (item) => {
    const cleanIp = item.client_ip?.replace(/^::ffff:/, '') || item.client_ip || 'N/A';
    const levelClasses = getLogLevelClasses(item.log_level);

    return (
      <>
        <span className="log-details-label">Zeitstempel:</span>
        <span className="log-details-value">{dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}</span>

        <span className="log-details-label">Hostname:</span>
        <span className="log-details-value">{item.hostname || 'N/A'}</span>

        <span className="log-details-label">Client IP:</span>
        <span className="log-details-value">{cleanIp}</span>

        <span className="log-details-label">Log Level:</span>
        <span className="log-details-value">
          <span className={`log-level ${levelClasses.badge}`}>
            {item.log_level?.toUpperCase() || 'UNKNOWN'}
          </span>
        </span>

        <span className="log-details-label">Modul:</span>
        <span className="log-details-value">{item.module || 'N/A'}</span>

        <span className="log-details-label">Process ID:</span>
        <span className="log-details-value">{item.process_id || 'N/A'}</span>

        <span className="log-details-label">Error Code:</span>
        <span className="log-details-value">{item.error_code || 'N/A'}</span>

        <span className="log-details-label">Request ID:</span>
        <span className="log-details-value">{item.request_id || 'N/A'}</span>

        <span className="log-details-label">Nachricht:</span>
        <span className="log-details-value log-message-full">{item.message || 'No message'}</span>
      </>
    );
  };

  return (
    <LogDisplay
      endpoint={endpoint}
      queryKey={queryKey}
      filters={filters}
      onFilterChange={handleFilterChange}
      validateFilters={validateFilters}
      clearFilters={clearFilters}
      renderLogItem={renderLogItem}
      renderLogDetails={renderLogDetails}
      FilterSection={ErrorLogFilters}
    />
  );
};

export default Index;
